import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    CartLineModel,
    CartModel,
    LanguageModel,
    OrderLineModel,
    ProductAutocompleteItemModel,
    ProductModel,
    WishListLineModel,
    WishListModel,
} from "@insite/client-framework/Types/ApiModels";
import PageProps from "@insite/client-framework/Types/PageProps";
import { CartPageContext } from "@insite/content-library/Pages/CartPage";
import { CheckoutReviewAndSubmitPageContext } from "@insite/content-library/Pages/CheckoutReviewAndSubmitPage";
import { OrderConfirmationPageContext } from "@insite/content-library/Pages/OrderConfirmationPage";
import { OrderDetailsPageContext } from "@insite/content-library/Pages/OrderDetailsPage";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import { ProductListPageContext } from "@insite/content-library/Pages/ProductListPage";
import { useCurrentLanguage } from "@store/Hooks/Context";

declare global {
    interface Window {
        dataLayer: [{}];
    }
}
interface DataLayerProps {
    event: string;
    ecommerce: object;
}

export const enum checkoutOption {
    shippingAddress = "Shipping Address",
    shippingAddressOneTime = "Shipping Address - One Time Shipping",
    reviewOrder = "Review Order Details",
    orderConfirmation = "Order Confirmation",
}

export const enum productAction {
    click = "Click",
    impression = "Impression",
    searchSuggestion = "SearchSuggestion",
}
export const enum productOption {
    productDetail = "Product Detail",
    catalogPage = "Catalog Page",
    productList = "Product List",
    productCarousel = "Product Carousel",
    searchList = "Search List",
    searchSuggestion = "Search Suggestion",
    orderHistory = "Order History",
    cart = "Cart",
    checkoutReview = "Checkout Review",
    checkoutConfirmation = "Checkout Confirmation",
}

interface BloomreachEvent {
    type: string;
    group: string;
    eType?: string;
    data: object;
}

const windowExist = typeof window !== "undefined";
const productIndex = 3;

export const push = (data: DataLayerProps) => {
    if (windowExist && Object.prototype.hasOwnProperty.call(window, "dataLayer")) {
        window.dataLayer.push(data);
    }
};

export const resolveCurrentPageContext = (currentPage: PageProps, isCategoryPage: boolean, isSearchPage: boolean) => {
    switch (currentPage.type) {
        case ProductDetailsPageContext:
            return productOption.productDetail;

        case ProductListPageContext:
            if (isSearchPage) {
                return productOption.searchList;
            }
            if (isCategoryPage) {
                return productOption.productList;
            }
            break;

        case "CategoryDetailsPage":
            return productOption.catalogPage;

        case OrderDetailsPageContext:
            return productOption.orderHistory;

        case CartPageContext:
            return productOption.cart;

        case CheckoutReviewAndSubmitPageContext:
            return productOption.checkoutReview;

        case OrderConfirmationPageContext:
            return productOption.checkoutConfirmation;

        default:
            return productOption.productCarousel;
    }
};

export const resolveCategory = () => {
    if (windowExist) {
        const resolvedCategory = window.location.pathname
            .slice(1)
            .replace(/-/gu, " ")
            .split("/")
            .filter((category, i) => i !== 0);
        if (resolvedCategory.length === productIndex) {
            resolvedCategory.pop();
        }
        return resolvedCategory.join("/");
    }
};

export const resolveVariant = (title: any) => title?.replace(/[sS]*.*( - )/, "") || "";

const formatPrice = (price?: number) => price?.toFixed(2) ?? "";

const mapCartLines = (cartLines?: CartLineModel[] | null): Array<object> =>
    cartLines?.map((cartLine: CartLineModel) => {
        const price = cartLine.pricing?.unitNetPrice.toFixed(2);
        return {
            name: cartLine.shortDescription,
            id: cartLine.erpNumber,
            ...(price ? { price } : {}),
            quantity: cartLine.qtyOrdered,
            variant: resolveVariant(cartLine.shortDescription),
        };
    }) || [];

// ---------------------------------------- //
// -- IMPRESSION -------------------------- //
// ---------------------------------------- //
const mapProductImpression = (
    products: ProductContextModel[] | undefined,
    productOptionType: string,
    category: string,
) => {
    return products?.map((product, position) => {
        return {
            name: product.product.productTitle,
            id: product.product.productNumber,
            price: product.productInfo ? formatPrice(product.productInfo.pricing?.unitListPrice) : 0,
            category,
            list: productOptionType,
            position: position + 1,
        };
});
};

// -- IMPRESSIONS ------------ //
export const dispatchProductImpressions = (
    products: ProductContextModel[] | undefined,
    currency: string | undefined,
    currentPage: PageProps,
    isCategoryPage: boolean,
    isSearchPage: boolean,
    category: string,
) => {
    if (products?.length === 0) {
        return;
    }
    const productOptionType = resolveCurrentPageContext(currentPage, isCategoryPage, isSearchPage) || "";

    push({
        event: "productImpression",
        ecommerce: {
            currencyCode: currency ?? "",
            impressions: mapProductImpression(products, productOptionType, category),
        },
    });
};
// -- DETAIL ------------ //
export const dispatchProductDetailEvent = (
    product: ProductContextModel, 
    category: string,
    currency: string | undefined,
) => {
    push({
        event: "productDetail",
        ecommerce: {
            currencyCode: currency ?? "",
            detail: {
                // actionField: {'list': 'Apparel Gallery'},
                products: mapProductImpression([product], productOption.productDetail, category),
            },
        },
    });
};


// ---------------------------------------- //
// -- CLICK ------------------------------- //
// ---------------------------------------- //
// Poolcorp Custom Code: Will eventually need to support other models
// like WishlistLineModel, InvoiceLineModel for example
const mapProductClick = (
    products: (CartLineModel | ProductContextModel | OrderLineModel)[],
    position: number,
    addQty = false,
) => {
    return products?.map((product, position) => {
        const productName =
            (product as ProductContextModel)?.product?.productTitle ||
            (product as CartLineModel)?.productName ||
            (product as OrderLineModel)?.shortDescription;
        const productId =
            (product as ProductContextModel)?.product?.productNumber ||
            (product as CartLineModel)?.erpNumber ||
            (product as OrderLineModel)?.productErpNumber;
        const unitListPrice =
            (product as ProductContextModel)?.productInfo?.pricing?.unitListPrice ||
            (product as OrderLineModel)?.unitListPrice ||
            (product as CartLineModel)?.pricing?.unitListPrice ||
            0;
        const brand =
            (product as ProductContextModel)?.product?.brand?.name ||
            (product as OrderLineModel)?.brand ||
            (product as CartLineModel)?.brand;

        const data = {
            name: productName,
            id: productId,
            price: formatPrice(unitListPrice),
            brand: brand ?? "",
            category: "",
            position: position + 1,
        };
        if (addQty) {
            const quantity =
                (product as ProductContextModel)?.productInfo?.qtyOrdered ||
                (product as CartLineModel)?.qtyOrdered ||
                (product as OrderLineModel)?.qtyOrdered;
            const dataWithQty = { ...data, quantity };
            return dataWithQty;
        }
        return data;
    });
};
// -- PRODUCT CLICK ------------ //
export const dispatchProductClickEvent = (
    product: CartLineModel | ProductContextModel | OrderLineModel,
    position: number,
    currentPage: PageProps,
    isCategoryPage: boolean,
    isSearchPage: boolean,
    currency: string | undefined,
) => {
    const productOptionType = resolveCurrentPageContext(currentPage, isCategoryPage, isSearchPage) || "";
    push({
        event: "productClick",
        ecommerce: {
            currencyCode: currency ?? "",
            click: {
                actionField: {
                    list: productOptionType,
                },
                products: mapProductClick([product], position),
            },
        },
    });
};
// -- SEARCHED CLICK ------------ //
const mapAutoCompleteProduct = (product: ProductAutocompleteItemModel) => {
    return {
        name: product.title,
        id: product.erpNumber,
        brand: product.brandName ?? "",
        category: "",
    };
};
export const dispatchProductSearchedClickEvent = (product: ProductAutocompleteItemModel) => {
    push({
        event: "productClick",
        ecommerce: {
            click: {
                actionField: {
                    list: productOption.searchSuggestion,
                },
                products: [mapAutoCompleteProduct(product)],
            },
        },
    });
};

// -- ADD TO LIST ------------ //
export const dispatchAddToListEvent = (
    products: (ProductContextModel | CartLineModel)[],
    position: number,
    currency: string | undefined,
    currentPage: PageProps,
    isCategoryPage: boolean,
    isSearchPage: boolean,
) => {
    const productOptionType = resolveCurrentPageContext(currentPage, isCategoryPage, isSearchPage) || "";
    push({
        event: "addToList",
        ecommerce: {
            currencyCode: currency ?? "",
            actionField: {
                list: productOptionType,
            },
            products: mapProductClick(products, position, true),
        },
    });
};

// -- CHECKOUT ------------ //
export const dispatchCheckoutEvent = (stepNumber: number, optionType: string, cart: CartModel) => {
    push({
        event: "checkout",
        ecommerce: {
            checkout: {
                actionField: { step: stepNumber, option: optionType },
                products: mapCartLines(cart.cartLines) ?? [],
            },
        },
    });
};

// ---------------------------------------- //
// -- CART -------------------------------- //
// ---------------------------------------- //

// -- ADD TO CART ------------ //
export const dispatchAddToCartEventCartLine = (
    currentLanguage?: LanguageModel | null,
    cartLineModel?: CartLineModel,
) => {
    if (cartLineModel && currentLanguage) {
        const productId = cartLineModel.productId + currentLanguage?.languageCode;
        const productSku = cartLineModel.properties.fullErpNumber || cartLineModel.erpNumber;
        const productName = cartLineModel.productName || cartLineModel.shortDescription;
        dispatchAddToCartEventBR(productId, productSku, productName);
    }
};

export const dispatchAddToCartEventOrderLine = (
    currentLanguage?: LanguageModel | null,
    orderLineModel?: OrderLineModel,
) => {
    if (orderLineModel && currentLanguage) {
        const productId = orderLineModel.productId + currentLanguage?.languageCode;
        const productSku = orderLineModel.properties.fullErpNumber || orderLineModel.productErpNumber;
        const productName = orderLineModel.productName || orderLineModel.shortDescription;
        dispatchAddToCartEventBR(productId, productSku, productName);
    }
};

export const dispatchAddToCartEventProduct = (
    currentLanguage?: LanguageModel | null,
    productModel?: ProductModel,
    productContext?: ProductContextModel,
    currency?: string | undefined,
) => {
    if (productModel && currentLanguage) {
        const productId = productModel.id + currentLanguage?.languageCode;
        const productSku = productModel.properties.fullErpNumber || productModel.productNumber;
        const productName = productModel.productTitle;
        dispatchAddToCartEventBR(productId, productSku, productName);
    }
};

const dispatchAddToCartEventBR = (productId: string, productSku: string, productName: string) => {
    if (productId && productSku && productName) {
        const brEvData: BloomreachEvent = {
            type: "cart",
            group: "click-add",
            data: { prod_id: productId, sku: productSku, prod_name: productName },
        };
        (window as any).BrTrk?.getTracker().logEvent(brEvData.type, brEvData.group, brEvData.data);
    }
};

export const dispatchAddToCartEvent = (cartLines: CartLineModel[], currency: string | undefined) => {
    push({
        event: "addToCart",
        ecommerce: {
            currencyCode: currency ?? "",
            add: {
                products: mapCartLines(cartLines),
            },
        },
    });
};
export const dispatchAddToCartFromWishListEvent = (
    cartLines: CartLineModel[],
    currency: string | undefined,
) => {
    push({
        event: "addToCart",
        ecommerce: {
            currencyCode: currency ?? "",
            add: {
                products: mapCartLines(cartLines),
            },
        },
    });
};
// -- REMOVE FROM CART ------------ //

export const dispatchDeleteProduct = (cartLines: CartLineModel[]) => {
    push({
        event: "removeFromCart",
        ecommerce: {
            remove: {
                products: mapCartLines(cartLines as CartLineModel[]),
            },
        },
    });
};


// ---------------------------------------- //
// -- SEARCH ------------------------------ //
// ---------------------------------------- //

// -- SEARCH ------------ //
export const dispatchSearchEvent = (searchTerm: string, catalogs: any[]) => {
    const brEvData: BloomreachEvent = {
        type: "suggest",
        group: "submit",
        data: { q: searchTerm, catalogs },
    };
    (window as any).BrTrk?.getTracker().logEvent(brEvData.type, brEvData.group, brEvData.data, {}, true);
};

// -- SUGGEST ------------ //
export const dispatchSuggestEvent = (searchTerm: string, suggestedTerm: string, catalogs: any[]) => {
    const brEvData: BloomreachEvent = {
        type: "suggest",
        group: "click",
        data: { aq: searchTerm, q: suggestedTerm, catalogs },
    };
    (window as any).BrTrk?.getTracker().logEvent(brEvData.type, brEvData.group, brEvData.data, {}, true);
};

// -- QUICK VIEW ------------ //
export const productQuickViewEvent = (productContext: ProductContextModel) => {
    const { product } = productContext;
    const currentLanguage = useCurrentLanguage();

    const brEvData: BloomreachEvent = {
        type: "product",
        group: "quickview",
        data: {
            prod_id: product.id + currentLanguage?.languageCode,
            prod_name: product.productTitle,
            sku: product.properties.fullErpNumber,
        },
    };
    (window as any).BrTrk?.getTracker().logEvent(brEvData.type, brEvData.group, brEvData.data);
};

// -- PURCHASE ------------ //
export const dispatchPurchaseEvent = (cart: CartModel) => {
    push({
        event: "purchase",
        ecommerce: {
            purchase: {
                actionField: {
                    id: cart.orderNumber,
                    affiliation: cart.billTo?.customerName ?? "Online Store",
                    revenue: formatPrice(cart.orderSubTotal),
                    tax: formatPrice(cart.totalTax),
                    shipping: formatPrice(cart.shippingAndHandling),
                },
                products: mapCartLines(cart.cartLines) ?? [],
            },
        },
    });
};
